<template>
  <div class="space-y-2 @container">
    <div class="grid grid-cols-1 gap-3 @md:grid-cols-3">
      <div v-if="!inFlowGraph">
        <label class="mb-1 block text-xs text-gray-400">Property</label>
        <HassSelector
          v-model="propertySelection"
          :settings="settings"
          :selector="resolvedPropertySelector" />
      </div>

      <div v-if="!inFlowGraph">
        <div class="mb-1 flex items-center justify-between gap-2">
          <label class="text-xs text-gray-400">Entity</label>
          <button
            v-if="hasExplicitEntity"
            type="button"
            class="text-xs text-cyan-400 hover:text-cyan-300"
            @click="usePrimaryEntity">
            Use Primary Entity
          </button>
        </div>
        <EntitySelector v-model="entitySelection" :settings="settings" placeholder="Primary Entity or explicit entity" />
        <p v-if="isDeprecatedTemplateSource" class="mt-1 text-xs text-amber-400">
          Select a concrete Home Assistant entity.
        </p>
      </div>

      <div>
        <label class="mb-1 block text-xs text-gray-400">State / Attribute</label>
        <BuilderSelect class="w-full text-sm px-1 py-1" v-model="valueMode">
          <option value="state">State</option>
          <option value="attribute">Attribute</option>
        </BuilderSelect>
      </div>

      <div v-if="valueMode === 'attribute'" class="@md:col-span-3">
        <label class="mb-1 block text-xs text-gray-400">Attribute</label>
        <div class="grid grid-cols-1 gap-2 @md:grid-cols-2">
          <HassSelector
            v-model="attributeSelection"
            :settings="settings"
            :selector="attributeSelector" />
          <BuilderInput
            type="text"
            class="w-full text-sm px-2 py-1"
            placeholder="Custom attribute"
            v-model="attributeValue" />
        </div>
      </div>
    </div>

    <div class="border-t border-slate-700 pt-2">
      <button type="button" class="flex items-center gap-1.5 text-xs text-cyan-400 hover:text-cyan-300"
        :aria-expanded="advancedOpen" @click="advancedOpen = !advancedOpen">
        <span class="inline-block transition-transform" :class="{ 'rotate-90': advancedOpen }">▸</span>
        Advanced
        <span v-if="advancedActive" class="text-cyan-300">· configured</span>
      </button>
      <div v-if="advancedOpen" class="mt-2 space-y-2 rounded border border-slate-700/70 bg-slate-800/20 p-2">
        <div v-if="showStrategySelector">
          <label class="mb-1 block text-xs text-gray-400">Strategy</label>
          <BuilderSelect class="w-full max-w-xs text-sm px-1 py-1" v-model="binding.strategy">
            <option v-for="key in strategyOptions" :key="key" :value="key">
              {{ BINDING_STRATEGIES[key]?.label ?? key }}
            </option>
          </BuilderSelect>
          <button type="button" class="mt-1 text-xs text-cyan-400 hover:text-cyan-300"
            @click="binding.strategy = ADVANCED_STRATEGY.key">
            Advanced Template…
          </button>
        </div>
        <CollapsibleTransformsSection :pipeline="binding.transforms" />
        <FormatterEditor :formatter="binding.formatter" />
        <div>
          <label class="mb-1 block text-xs text-gray-400">Custom Expression</label>
          <BuilderInput type="text" class="w-full text-sm px-2 py-1" placeholder="| round(1)"
            v-model="binding.filter" />
          <p class="mt-1 text-xs text-gray-500">Use only when the structured builder cannot express the desired result.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/**
 * StateBindingEditor.vue
 *
 * Editor for the 'state' binding strategy — an entity, optional attribute,
 * the shared value-transform pipeline, a Formatter, and a collapsed
 * Custom Expression escape hatch. Resolved and rendered by
 * BindingEditorRouter.vue; never referenced by name from StateBindingsTable.
 */
import { computed, ref, watch } from 'vue';
import { BINDING_STRATEGIES, ADVANCED_STRATEGY } from '../../../Hasp/properties/v2/bindings.js';
import {
  getAttributeSuggestions,
  getAttributeSuggestionsForSchema,
} from '../../../utils/hassStateAttributes.js';
import HassSelector from '../HassSelector.vue';
import { HomeAssistantMetadataAdapter } from '../metadata/HomeAssistantMetadataAdapter.js';
import { DesignerSchemaAdapter } from '../metadata/DesignerSchemaAdapter.js';
import { getEffectivePrimaryEntity } from '../metadata/effectiveEntity.js';
import { COLOR_ATTRIBUTES } from '../../../automation/schema/colorNormalizer.js';
import { getPropertyEditorMeta } from '../../../automation/schema/PropertyEditorMeta.js';
import EntitySelector from '../EntitySelector.vue';
import BuilderSelect from './BuilderSelect.vue';
import BuilderInput from './BuilderInput.vue';
import CollapsibleTransformsSection from './CollapsibleTransformsSection.vue';
import FormatterEditor from './FormatterEditor.vue';

const props = defineProps({
  binding:     { type: Object, required: true }, // AutomationBinding — mutated directly
  node:        { type: Object, required: true },
  settings:    { type: Object, required: true },
  editorState: { type: Object, required: true },  // BindingEditorState — unused here, kept for a uniform editor prop shape
  strategyOptions: { type: Array, default: () => [] },
  showStrategySelector: { type: Boolean, default: false },
  propertySelector: { type: Object, default: null },
  // In a Home Assistant flow graph the property and the entity are chosen by
  // the binding node and its wires, not here.
  inFlowGraph: { type: Boolean, default: false },
});

const entityInfo = ref(null);
const metadataAdapter = new HomeAssistantMetadataAdapter(props.settings);
const schemaAdapter = DesignerSchemaAdapter.forNode(props.node);
const resolvedPropertySelector = computed(() => props.propertySelector ?? schemaAdapter.selectorForProperty(props.node.hass_properties ?? []));
const propertySelection = computed({
  get: () => props.binding.property,
  set(property) {
    props.binding.property = property;
  },
});
const isDeprecatedTemplateSource = computed(() => props.binding.sourceType === 'template');
const entitySelection = computed({
  // An inherited primary entity is a display/default value only. It is not
  // written into the binding until the user explicitly selects an entity.
  get: () => props.binding.entityId || getEffectivePrimaryEntity(props.node),
  set(value) {
    props.binding.entityId = value;
    props.binding.sourceType = 'entity';
  },
});
const bindingEntityId = computed(() => props.binding.entityId || getEffectivePrimaryEntity(props.node));
const hasExplicitEntity = computed(() => Boolean(String(props.binding.entityId ?? '').trim()));

function usePrimaryEntity() {
  delete props.binding.entityId;
  props.binding.sourceType = 'entity';
}

const bindingDomain = computed(() => bindingEntityId.value.split('.')[0] ?? '');

const fallbackAttributes = computed(() => {
  const schemaSuggestions = getAttributeSuggestionsForSchema(
    props.binding.property,
    props.node.schemaV2,
    bindingDomain.value,
  );
  const entitySuggestions = getAttributeSuggestions(props.binding.property, bindingEntityId.value);
  const seen = new Set();
  return [...schemaSuggestions, ...entitySuggestions]
    .filter(suggestion => suggestion.attribute && !seen.has(suggestion.attribute))
    .map(suggestion => {
      seen.add(suggestion.attribute);
      return { attribute: suggestion.attribute, label: suggestion.label };
    });
});

const attributeSuggestions = computed(() => {
  const metadataAttributes = Object.keys(entityInfo.value?.attributes ?? {})
    .filter(Boolean);
  const isColorProperty = getPropertyEditorMeta(props.node.schemaV2, props.binding.property).editor === 'color';
  const names = [
    ...fallbackAttributes.value.map(s => s.attribute),
    ...metadataAttributes,
    ...(isColorProperty && bindingEntityId.value ? COLOR_ATTRIBUTES : []),
    ...(props.binding.valueSource === 'attribute' && props.binding.attribute ? [props.binding.attribute] : []),
  ];
  const unique = [...new Set(names)];
  return unique
    .map(attribute => ({
      attribute,
      label: fallbackAttributes.value.find(s => s.attribute === attribute)?.label ?? attribute,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
});

const attributeSelector = computed(() => ({
  kind: 'attribute',
  entityId: bindingEntityId.value,
  options: attributeSuggestions.value.map(suggestion => ({
    value: suggestion.attribute,
    label: suggestion.label,
  })),
  preserveUnknown: true,
}));

const updateAttributeFormatter = attribute => {
  // A colour property must receive LVGL's hex representation. For ordinary
  // text properties the Color formatter remains an explicit user choice.
  const isColorProperty = getPropertyEditorMeta(props.node.schemaV2, props.binding.property).editor === 'color';
  if (isColorProperty && COLOR_ATTRIBUTES.includes(attribute)) {
    props.binding.formatter.type = 'color';
    props.binding.formatter.params = {};
  } else if (isColorProperty && props.binding.formatter.type === 'color') {
    props.binding.formatter.type = 'none';
    props.binding.formatter.params = {};
  }
};

const valueMode = computed({
  get: () => props.binding.valueSource === 'attribute' ? 'attribute' : 'state',
  set(mode) {
    if (mode === 'attribute') {
      props.binding.valueSource = 'attribute';
      updateAttributeFormatter(props.binding.attribute);
      return;
    }

    props.binding.valueSource = 'state';
    props.binding.attribute = '';
    if (props.binding.formatter.type === 'color') {
      props.binding.formatter.type = 'none';
      props.binding.formatter.params = {};
    }
  },
});

const attributeSelection = computed({
  get: () => props.binding.attribute,
  set(attribute) {
    props.binding.valueSource = 'attribute';
    props.binding.attribute = attribute;
    updateAttributeFormatter(attribute);
  },
});

// Keep the free-text field separate from the suggestion selector. This makes
// custom HA attributes possible even when no preview source or HA metadata is
// available, while the selector remains a convenience rather than a storage
// format of its own.
const attributeValue = computed({
  get: () => props.binding.attribute,
  set(attribute) {
    props.binding.valueSource = 'attribute';
    props.binding.attribute = attribute;
    updateAttributeFormatter(attribute);
  },
});

watch(
  () => [
    bindingEntityId.value,
    props.settings?.tauri,
    props.settings?.hass?.protocol,
    props.settings?.hass?.host,
    props.settings?.hass?.port,
    props.settings?.hass?.token,
  ],
  async ([entityId]) => {
    entityInfo.value = null;
    if (!entityId || !props.settings?.tauri) return;
    try {
      metadataAdapter.setSettings(props.settings);
      await metadataAdapter.loadEntities();
      entityInfo.value = metadataAdapter.entity(entityId);
    } catch {
      // The text entity selector remains usable when HA metadata is offline.
    }
  },
  { immediate: true },
);

watch(
  () => props.binding.property,
  () => {
    const isColorProperty = getPropertyEditorMeta(props.node.schemaV2, props.binding.property).editor === 'color';
    if (!isColorProperty && props.binding.formatter.type === 'color') {
      props.binding.formatter.type = 'none';
      props.binding.formatter.params = {};
    }
  },
);

const advancedOpen = ref(Boolean(
  props.binding.filter ||
  props.binding.fallback ||
  props.binding.transforms?.length ||
  (props.binding.formatter?.type && props.binding.formatter.type !== 'none'),
));
const advancedActive = computed(() => Boolean(
  props.binding.filter ||
  props.binding.fallback ||
  props.binding.transforms?.length ||
  (props.binding.formatter?.type && props.binding.formatter.type !== 'none'),
));
</script>
